import { SummarySection } from '../summary/data-source.decorator';

/** A client-resolved grounding source: its data rides inline because the server can't reach it. */
interface DeviceNode {
  kind: 'device';
  label?: string;
  data: unknown;
}

/**
 * Assemble the grounding forest a summary agent consumes: the authored `groundingConfig`'s
 * server-side source specs pass through untouched, and the client-resolved `sections` are appended
 * as `kind:'device'` nodes with their data inline. The result is ASCII-only JSON (see {@link asciiJson}),
 * because the agent forwards it to the grounding MCP as the `x-grounding-config` header.
 */
export function buildForest(rawConfig: string | undefined, sections: SummarySection[]): string {
  let forest: { sources?: unknown[]; [k: string]: unknown } = {};
  if (rawConfig) {
    try { forest = (JSON.parse(rawConfig) as typeof forest) ?? {}; } catch { forest = {}; }
  }
  const authored = Array.isArray(forest.sources) ? forest.sources : [];
  const serverSources = authored.filter(s => !!s && (s as { kind?: unknown }).kind !== 'device');
  const deviceSources: DeviceNode[] = sections.map(s => ({ kind: 'device', label: s.title, data: s.data }));
  return asciiJson({ ...forest, sources: [...serverSources, ...deviceSources] });
}

/**
 * `JSON.stringify` with every character outside printable ASCII written as a `\uXXXX` escape, so the
 * text is a valid HTTP header value and still parses back to the original value. Foundry rejects a
 * tool header containing any other character with "Connection header ... contains an invalid value".
 */
export function asciiJson(value: unknown): string {
  return JSON.stringify(value).replace(/[\u007f-￿]/g, c => `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`);
}

/**
 * The canonical empty forest: a valid, parseable grounding payload with no sources. Agent-mode calls
 * send this when there's nothing to ground, so `groundingConfig` is always present AND the grounding
 * MCP can parse it (a raw null/empty string would crash `fetch_grounded_data`).
 */
export const EMPTY_FOREST = buildForest(undefined, []);

/**
 * Model-mode delivery: render a forest's `kind:'device'` nodes into one labelled block, laid out
 * as `=== label ===` + JSON. Returns undefined when there's no forest or no device data to surface.
 */
export function renderDeviceContext(rawConfig: unknown): string | undefined {
  if (typeof rawConfig !== 'string' || !rawConfig.trim()) return undefined;
  let forest: { sources?: unknown[] };
  try {
    forest = JSON.parse(rawConfig) as { sources?: unknown[] };
  } catch {
    return undefined;
  }
  const sources = Array.isArray(forest?.sources) ? forest.sources : [];
  const blocks: string[] = [];
  for (const s of sources) {
    const node = s as Partial<DeviceNode> | null;
    if (!node || node.kind !== 'device' || isEmpty(node.data)) continue;
    const title = (node.label ?? '').trim();
    const text = typeof node.data === 'string' ? node.data : JSON.stringify(node.data, null, 2);
    blocks.push(`=== ${title} ===\n${text}`);
  }
  return blocks.length ? blocks.join('\n\n') : undefined;
}

/** True when a device source carries nothing worth grounding on (null / empty string, array, object). */
function isEmpty(data: unknown): boolean {
  if (data === undefined || data === null || data === '') return true;
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === 'object') return Object.keys(data as object).length === 0;
  return false;
}
